"use client";

import Link from "next/link";
import { Bell } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";

const POLL_INTERVAL_MS = 60_000;

/**
 * EPIC-006 STORY-606 — header bell icon with unread count badge.
 *
 * Polls every 60s for the unread count. Click navigates to
 * /notifications which shows the full inbox + mark-as-read controls.
 * The polling is cheap (one indexed COUNT(*)); a future WebSocket
 * push channel can replace this when EPIC-009 mobile work ships
 * push infrastructure that the web app can also subscribe to.
 */
export function NotificationsBell() {
  const { t } = useTranslation();
  const [unread, setUnread] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const refresh = () => {
      void api
        .getUnreadNotificationCount()
        .then((res) => {
          if (!cancelled) setUnread(res.count);
        })
        .catch(() => {
          // Silent — keep showing whatever we had.
        });
    };
    refresh();
    timer = setInterval(refresh, POLL_INTERVAL_MS);

    // Re-poll on focus so the user gets a fresh count when they
    // come back to the tab.
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return (
    <Link
      href="/notifications"
      aria-label={
        unread && unread > 0
          ? t("notificationsBell.ariaLabelWithCount", { count: unread })
          : t("notificationsBell.ariaLabel")
      }
      className="relative rounded-md px-2 py-1.5 text-[var(--color-text2)] transition-colors hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]"
      title={t("notificationsBell.title")}
    >
      <Bell size={16} strokeWidth={1.75} aria-hidden="true" />

      {unread !== null && unread > 0 && (
        <span
          className="absolute -right-0.5 -top-0.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-bold text-white"
          style={{ background: "var(--color-no)" }}
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </Link>
  );
}
